import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, ExternalLink, FileText, Newspaper, Building2, RefreshCw, AlertCircle } from "lucide-react";
import { fmtDate } from "../lib/format";

function SourceIcon({ type }) {
  if (type === "news") return <Newspaper className="w-3.5 h-3.5" />;
  if (type === "filing") return <FileText className="w-3.5 h-3.5" />;
  return <Building2 className="w-3.5 h-3.5" />;
}

export default function InsightModal({ open, onClose, stock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState("pt");

  const stockId = stock?.id;

  const fetchInsight = useCallback(async () => {
    if (!stockId) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const r = await apiClient.get(`/stocks/${stockId}/insight`, {
        params: { language: lang },
        timeout: 90000, // Claude can be slow — allow up to 90s
      });
      setData(r.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg;
      if (status === 404) {
        msg = detail || "Ainda não há dados suficientes para gerar a nota desta ação.";
      } else if (e.code === "ECONNABORTED") {
        msg = "A geração demorou demais. Tente novamente em instantes.";
      } else {
        msg = detail || "Falha ao gerar a nota. Tente novamente.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [stockId, lang]);

  useEffect(() => {
    if (!open || !stockId) return;
    fetchInsight();
  }, [open, stockId, lang, fetchInsight]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-y-auto"
        data-testid="insight-modal"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-display text-2xl">
                Nota Analítica · {stock?.ticker}
              </DialogTitle>
              <DialogDescription>
                Geração por IA baseada em fontes verificáveis (Reuters, Bloomberg,
                Valor, InfoMoney, FT, CNBC) e documentos oficiais (SEC EDGAR / CVM).
              </DialogDescription>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant={lang === "pt" ? "default" : "outline"}
                size="sm"
                onClick={() => setLang("pt")}
                data-testid="insight-lang-pt"
              >
                PT
              </Button>
              <Button
                variant={lang === "en" ? "default" : "outline"}
                size="sm"
                onClick={() => setLang("en")}
                data-testid="insight-lang-en"
              >
                EN
              </Button>
            </div>
          </div>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Gerando nota com Claude Sonnet 4.5…
          </div>
        )}

        {error && !loading && (
          <div
            className="rounded-md border border-sell/40 bg-sell-soft/30 p-6 text-center space-y-3"
            data-testid="insight-error"
          >
            <AlertCircle className="w-6 h-6 mx-auto text-sell-strong" />
            <div className="text-sm text-sell-strong font-medium">{error}</div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchInsight}
              data-testid="insight-retry"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Tentar novamente
            </Button>
          </div>
        )}

        {data && (
          <div className="space-y-6 mt-2">
            <div className="rounded-md bg-muted/40 border p-5">
              <h3 className="font-display text-lg font-semibold mb-3">{data.title}</h3>
              <div className="prose prose-sm prose-stone max-w-none text-foreground/90 whitespace-pre-wrap leading-relaxed">
                {data.body}
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                USD/BRL referência: {Number(data.usd_brl_reference_rate || 0).toFixed(4)} ·
                Gerada em {fmtDate(data.generated_at)} · Idioma {data.language.toUpperCase()}
              </div>
            </div>

            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
                Fontes
              </h4>
              <div className="space-y-2">
                {(data.sources || []).map((s, idx) => (
                  <a
                    key={idx}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-start gap-3 rounded-md border p-3 hover:bg-accent transition"
                    data-testid={`insight-source-${idx}`}
                  >
                    <div className="mt-0.5 text-muted-foreground">
                      <SourceIcon type={s.source_type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.15em]">
                          {s.source_name}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {fmtDate(s.published_at)}
                        </span>
                      </div>
                      <div className="text-sm mt-1 truncate">{s.title}</div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
